import { CommonModule } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AxsIconComponent } from '@vanguard/axs-angular';
import { transformAssetValue } from '@vanguard/axs-core';

@Component({
  selector: 'ngx-input-number',
  templateUrl: './input-number.component.html',
  styleUrl: './input-number.component.scss',
  standalone: true,
  imports: [CommonModule, FormsModule, AxsIconComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AxsInputNumberComponent implements OnInit, OnChanges {
  @Input() ariaLabelId: string = 'ariaLabelId';
  @Input() inputType: AssetInputType = 'number';
  @Input() name: string = '';
  @Input() id: string = '';
  @Input() defaultValue: string | undefined = undefined;
  @Input() currentValue: String = ''; // eslint-disable-line @typescript-eslint/no-wrapper-object-types
  @Input() hintText: string = '';
  @Input() differsText: string = '';
  @Input() differsLinkText: string = '';
  @Input() differsLink: string = '';
  @Input() errorText: string = '';
  @Input() decimalAmount: number = 0;
  @Input() noNegative: boolean = true;
  @Input() designType: string = 'outlined';
  @Input() max?: number;

  @Output() onInputChange = new EventEmitter<any>();

  get defaultTransformOptions() {
    return {
      decimalAmount: this.decimalAmount,
      noNegative: this.noNegative,
    };
  }

  get placeholder() {
    return this.inputType === 'dollar' ? '$' : this.inputType === 'percent' ? '%' : '';
  }

  get inputError() {
    return this.errorText != '';
  }

  get inputDiffers() {
    if (this.defaultValue != undefined) {
      return (
        transformAssetValue(this.currentValue.toString(), {
          ...this.defaultTransformOptions,
        }).rawValue !=
        transformAssetValue(this.defaultValue, {
          ...this.defaultTransformOptions,
        }).rawValue
      );
    }
    return false;
  }

  private formatInitialValue() {
    if (!this.currentValue || this.currentValue.toString().trim() === '' || this.currentValue.toString() === 'NaN') {
      this.currentValue = '';
      return;
    }

    const stringValue = this.currentValue.toString();

    if (this.inputType === 'dollar') {
      const asset = transformAssetValue(stringValue, {
        ...this.defaultTransformOptions,
        prefix: '$',
      });
      this.currentValue = asset.logicalValue;
    }
  }

  ngOnInit() {
    this.formatInitialValue();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['currentValue'] && !changes['currentValue'].firstChange) {
      this.formatInitialValue();
    }
  }

  // eslint-disable-next-line max-statements
  onInput($event: Event) {
    if (!$event.target && !($event.target as unknown as HTMLInputElement)) {
      return;
    }

    const input = $event.target as HTMLInputElement;

    const rawValue = this.transformAssetValue(input.value).rawValue;
    // removes leading 0s from input, re-adds negative sign if any
    const parsedAsset = this.transformAssetValue(rawValue.replace(/^(-?)0+(?=\d)/g, '$1'));

    let finalValue = parseFloat(parsedAsset.rawValue);

    // Enforce max limit if defined
    if (this.max !== undefined && !isNaN(finalValue) && finalValue > this.max) {
      finalValue = this.max;
      const maxAsset = this.transformAssetValue(finalValue.toString());
      input.value = maxAsset.logicalValue;
      this.currentValue = input.value;
    } else {
      input.value = parsedAsset.logicalValue;
      this.currentValue = input.value;
    }

    this.onInputChange.emit(finalValue);
  }

  transformAssetValue(inputValue: any, forDisplay: boolean = false) {
    return transformAssetValue(inputValue, {
      ...this.defaultTransformOptions,
      ...this.inputType === 'dollar' ? { prefix: '$' } : {},
      ...this.inputType === 'percent' && forDisplay ? { suffix: '%' } : {},
    });
  }

  get currentDisplayValue(): string | null {
    if (this.inputType === 'number') {
      return this.currentValue.toString().length > 3 ?
        this.transformAssetValue(this.currentValue.toString(), true).displayValue : null;
    }
    return this.transformAssetValue(this.currentValue.toString(), true).displayValue;
  }

  differsLinkClick(){
    window.open(this.differsLink, '_blank', 'noopener,noreferrer');
  }
}

export type AssetInputType = 'number' | 'percent' | 'dollar';
